import React, { useEffect, useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Check, X, Star, ExternalLink, Edit3, Save, Plus } from "lucide-react";
import AdminQuickAddModel from "@/components/AdminQuickAddModel";
import AdminCastingCallsTab from "@/components/AdminCastingCallsTab";
import AdminProjectsTab from "@/components/AdminProjectsTab";
import AdminCastingApplicationsTab from "@/components/AdminCastingApplicationsTab";
import AdminEditProfileModal from "@/components/AdminEditProfileModal";
import AdminSiteStatsTab from "@/components/AdminSiteStatsTab";

const WORK_FIELDS = [
  { k: "title", l: "Title" },
  { k: "artist", l: "Artist" },
  { k: "featuring", l: "Featuring" },
  { k: "music", l: "Music" },
  { k: "lyrics", l: "Lyrics" },
  { k: "label", l: "Label" },
  { k: "casting_director", l: "Casting Director" },
  { k: "views", l: "Views" },
  { k: "youtube_url", l: "YouTube URL" },
];

function WorkEditor({ w, onSave }) {
  const [form, setForm] = useState({});
  const [open, setOpen] = useState(false);
  useEffect(() => { setForm({}); }, [w.work_id]);
  const set = (k, v) => setForm({ ...form, [k]: v });
  const save = async () => {
    const patch = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== undefined));
    if (Object.keys(patch).length === 0) { setOpen(false); return; }
    try { await api.post("/admin/works/update", { work_id: w.work_id, ...patch }); toast.success("Saved"); onSave(); setOpen(false); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const val = (k) => form[k] !== undefined ? form[k] : (w[k] || "");

  return (
    <div className="sv-card p-5">
      <div className="flex justify-between flex-wrap gap-3 items-start">
        <div>
          <div className="font-mono-label text-[10px] text-[#C5A059]">Music Video</div>
          <div className="font-serif-editorial text-xl text-[#F4EFE4] mt-1">{w.title}</div>
          <div className="text-xs text-[#8A8478]">{w.artist}{w.featuring && ` · Featuring: ${w.featuring}`}</div>
          <div className="mt-2 flex items-center gap-3 text-[10px]">
            {w.youtube_url
              ? <span className="font-mono-label text-[#7BC46B]">● WATCH ACTIVE</span>
              : <span className="font-mono-label text-[#8A8478]">○ NO URL · Watch button hidden</span>}
            {w.featured_home && <span className="font-mono-label text-[#C5A059]">HOME</span>}
          </div>
        </div>
        <div className="flex gap-2">
          {w.youtube_url && (
            <a href={w.youtube_url} target="_blank" rel="noopener noreferrer" className="sv-btn-outline text-xs inline-flex items-center gap-1"><ExternalLink size={12} />Preview</a>
          )}
          <label className="flex items-center gap-2 text-xs text-[#CDC7BA] px-3 py-2 rounded-full border border-[rgba(197,160,89,0.35)]">
            <input type="checkbox" defaultChecked={w.featured_home} onChange={(e) => api.post("/admin/works/update", { work_id: w.work_id, featured_home: e.target.checked }).then(onSave)} /> Home
          </label>
          <button onClick={() => setOpen(!open)} className="sv-btn-outline text-xs inline-flex items-center gap-1" data-testid={`edit-work-${w.work_id}`}>
            <Edit3 size={12} />{open ? "Close" : "Edit"}
          </button>
        </div>
      </div>

      {open && (
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          {WORK_FIELDS.map((f) => (
            <div key={f.k}>
              <div className="font-mono-label text-[10px] text-[#C5A059] mb-1">{f.l}</div>
              <input className="sv-input text-xs" value={val(f.k)} onChange={(e) => set(f.k, e.target.value)} placeholder={f.l} />
            </div>
          ))}
          <div className="md:col-span-2 flex gap-2 justify-end pt-2">
            <button onClick={() => setOpen(false)} className="sv-btn-outline text-xs">Cancel</button>
            <button onClick={save} className="sv-btn-primary text-xs inline-flex items-center gap-1"><Save size={12} />Save</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const [tab, setTab] = useState("stats");
  const [stats, setStats] = useState({});
  const [profiles, setProfiles] = useState([]);
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [plans, setPlans] = useState([]);
  const [works, setWorks] = useState([]);
  const [applications, setApplications] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [pendingUrls, setPendingUrls] = useState([]);
  const [editingProfile, setEditingProfile] = useState(null);

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/profiles").then((r) => setProfiles(r.data)).catch(() => {});
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/plans").then((r) => setPlans(r.data));
    api.get("/works").then((r) => setWorks(r.data)).catch(() => {});
    api.get("/admin/applications").then((r) => setApplications(r.data)).catch(() => {});
    api.get("/admin/contact").then((r) => setContacts(r.data)).catch(() => {});
    api.get("/admin/pending-urls").then((r) => setPendingUrls(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const verify = async (uid, status) => { await api.post("/admin/verify", { user_id: uid, status }); toast.success(status); load(); };
  const feat = async (uid, featured) => { await api.post("/admin/featured", { user_id: uid, featured }); load(); };
  const delTalent = async (uid) => { if (!window.confirm("Delete this profile?")) return; await api.delete(`/admin/talents/${uid}`); toast.success("Deleted"); load(); };
  const updatePrice = async (plan_id, price) => { await api.post("/admin/plans", { plan_id, price: parseInt(price) }); toast.success("Price updated"); load(); };
  const decide = async (application_id, decision) => { await api.post("/admin/applications/decide", { application_id, decision }); toast.success(decision); load(); };
  const delPending = async (pid) => { await api.delete(`/admin/pending-urls/${pid}`); toast.success("Removed"); load(); };

  const tabs = ["stats", "add-model", "applications", "casting-applications", "profiles", "casting-calls", "projects", "site-stats", "pending-urls", "users", "orders", "plans", "contacts"];

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-[1440px] mx-auto px-4 sm:px-8 lg:px-12 py-12">
        <div className="sv-overline mb-3">Admin</div>
        <h1 className="font-serif-hero text-4xl text-[#F4EFE4]">SV Talent Console</h1>

        <div className="mt-6 flex gap-2 flex-wrap">
          {tabs.map((k) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
              className={`px-5 py-2 rounded-full text-xs font-mono-label ${tab === k ? "bg-[#C5A059] text-[#0A0A0B]" : "sv-card"}`}>{k}</button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "stats" && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(stats).map(([k, v]) => (
                <div key={k} className="sv-card p-6"><div className="font-mono-label text-[10px] text-[#C5A059]">{k.replace(/_/g, " ")}</div><div className="font-serif-hero text-3xl mt-1 text-[#F4EFE4]">{v}</div></div>
              ))}
            </div>
          )}

          {tab === "add-model" && (
            <AdminQuickAddModel onCreated={load} />
          )}

          {tab === "applications" && (
            <div className="space-y-3">
              {applications.length === 0 && <div className="text-sm text-[#8A8478]">No applications yet.</div>}
              {applications.map((a) => (
                <div key={a.application_id} className="sv-card p-5 flex flex-col md:flex-row md:items-center gap-4 justify-between">
                  <div className="flex gap-4 items-center">
                    {a.profile_image && <img src={a.profile_image} className="w-16 h-16 rounded object-cover" alt="" />}
                    <div>
                      <div className="font-serif-editorial text-lg text-[#F4EFE4]">{a.full_name}</div>
                      <div className="text-xs text-[#8A8478]">{a.category} · {a.city} · {a.email}</div>
                      <div className="text-[10px] font-mono-label text-[#C5A059] mt-1">Status: {a.status}</div>
                    </div>
                  </div>
                  {a.status === "pending" && (
                    <div className="flex gap-2">
                      <button onClick={() => decide(a.application_id, "approve")} className="sv-btn-primary text-xs inline-flex items-center gap-1"><Check size={13} />Approve</button>
                      <button onClick={() => decide(a.application_id, "reject")} className="sv-btn-outline text-xs inline-flex items-center gap-1"><X size={13} />Reject</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {tab === "profiles" && (
            <div className="space-y-3">
              {profiles.map((p) => (
                <div key={p.user_id} className="sv-card p-5 flex flex-col md:flex-row md:items-center gap-4 justify-between">
                  <div className="flex items-center gap-4">
                    {p.data?.profile_photo && <img src={p.data.profile_photo} className="w-14 h-14 rounded object-cover" alt="" />}
                    <div>
                      <div className="font-serif-editorial text-lg text-[#F4EFE4]">{p.full_name}</div>
                      <div className="text-xs text-[#8A8478]">{p.data?.city} · {p.data?.categories?.join(", ")} · {(p.data?.gallery || []).length} photos · {(p.data?.videos || []).length} videos</div>
                      <div className="text-xs font-mono-label text-[#C5A059] mt-1">Status: {p.status}{p.featured && " · Featured"} · /{p.data?.gender === "female" ? "women" : p.data?.gender === "male" ? "men" : "talent"}/{p.data?.slug || p.user_id}</div>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button onClick={() => setEditingProfile(p)} className="sv-btn-primary text-xs" data-testid={`profile-edit-${p.user_id}`}><Edit3 size={12} className="inline mr-1" />Edit</button>
                    <button onClick={() => feat(p.user_id, !p.featured)} className="sv-btn-outline text-xs inline-flex items-center gap-1"><Star size={12} />{p.featured ? "Unfeature" : "Feature"}</button>
                    <button onClick={() => verify(p.user_id, "verified")} className="sv-btn-outline text-xs">Verify</button>
                    <button onClick={() => delTalent(p.user_id)} className="sv-btn-outline text-xs inline-flex items-center gap-1"><Trash2 size={12} />Delete</button>
                  </div>
                </div>
              ))}
              {profiles.length === 0 && <div className="text-sm text-[#8A8478]">No profiles yet. Approve applications or use Add Model to populate the directory.</div>}
            </div>
          )}

          {tab === "site-stats" && <AdminSiteStatsTab />}

          {tab === "casting-applications" && <AdminCastingApplicationsTab />}
          {tab === "casting-calls" && <AdminCastingCallsTab />}
          {tab === "projects" && <AdminProjectsTab />}

          {tab === "works" && (
            <div className="space-y-4">
              <div className="text-xs text-[#8A8478] mb-2">Edit any field per project. Watch Project button on the public site activates automatically when a YouTube URL is saved.</div>
              {works.map((w) => <WorkEditor key={w.work_id} w={w} onSave={load} />)}
            </div>
          )}

          {tab === "pending-urls" && (
            <div className="space-y-3">
              <div className="sv-card p-5">
                <div className="font-mono-label text-[10px] text-[#C5A059]">Unassigned URLs</div>
                <div className="text-xs text-[#8A8478] mt-1 leading-relaxed">
                  These URLs were provided but their exact project identity is not confirmed. They are NOT auto-assigned. Once you confirm which song a URL belongs to, open Admin → Works, edit that project and paste the URL, then delete it from this list.
                </div>
              </div>
              {pendingUrls.length === 0 && <div className="text-sm text-[#8A8478]">No pending URLs.</div>}
              {pendingUrls.map((p) => (
                <div key={p.pending_id} className="sv-card p-4 flex flex-wrap gap-3 items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-sm text-[#F4EFE4] break-all hover:text-[#C5A059]">{p.url}</a>
                    <div className="text-[10px] font-mono-label text-[#8A8478] mt-1">{p.note}</div>
                  </div>
                  <div className="flex gap-2">
                    <a href={p.url} target="_blank" rel="noopener noreferrer" className="sv-btn-outline text-xs inline-flex items-center gap-1"><ExternalLink size={12} />Preview</a>
                    <button onClick={() => delPending(p.pending_id)} className="sv-btn-outline text-xs inline-flex items-center gap-1"><Trash2 size={12} />Remove</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "users" && (
            <div className="sv-card overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#131316]"><tr><th className="p-3 text-left text-[#C5A059] font-mono-label text-[10px]">Name</th><th className="p-3 text-left text-[#C5A059] font-mono-label text-[10px]">Email</th><th className="p-3 text-left text-[#C5A059] font-mono-label text-[10px]">Role</th><th className="p-3 text-left text-[#C5A059] font-mono-label text-[10px]">Plan</th></tr></thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.user_id} className="border-t border-[rgba(255,255,255,0.05)] text-[#CDC7BA]"><td className="p-3">{u.full_name}</td><td className="p-3">{u.email}</td><td className="p-3">{u.role}</td><td className="p-3">{u.subscription?.plan_name || "-"}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "orders" && (
            <div className="space-y-3">
              {orders.length === 0 && <div className="text-sm text-[#8A8478]">No orders yet.</div>}
              {orders.map((o) => (
                <div key={o.order_id} className="sv-card p-5 flex justify-between"><div><div className="font-serif-editorial text-[#F4EFE4]">{o.plan_name}</div><div className="text-xs text-[#8A8478]">{o.order_id}</div></div><div className="text-right text-[#CDC7BA]"><div>₹{o.total}</div><div className="text-xs font-mono-label text-[#C5A059]">{o.status}</div></div></div>
              ))}
            </div>
          )}

          {tab === "plans" && (
            <div className="grid md:grid-cols-3 gap-4">
              {plans.map((p) => (
                <div key={p.plan_id} className="sv-card p-6">
                  <div className="font-mono-label text-[10px] text-[#C5A059]">{p.plan_id}</div>
                  <div className="font-serif-hero text-3xl text-[#F4EFE4]">₹{p.price}</div>
                  <input type="number" defaultValue={p.price} onBlur={(e) => updatePrice(p.plan_id, e.target.value)} className="mt-4 sv-input" />
                  <div className="text-xs text-[#8A8478] mt-2">Blur to save.</div>
                </div>
              ))}
            </div>
          )}

          {tab === "contacts" && (
            <div className="space-y-3">
              {contacts.length === 0 && <div className="text-sm text-[#8A8478]">No casting/contact enquiries yet.</div>}
              {contacts.map((c) => (
                <div key={c.enquiry_id} className="sv-card p-5">
                  <div className="flex justify-between flex-wrap gap-2">
                    <div className="font-serif-editorial text-lg text-[#F4EFE4]">{c.name} <span className="text-sm text-[#8A8478]">· {c.company}</span></div>
                    <div className="text-xs font-mono-label text-[#C5A059]">{c.requirement}</div>
                  </div>
                  <div className="text-xs text-[#8A8478] mt-1">{c.email} · {c.phone}</div>
                  <div className="text-sm mt-2 text-[#CDC7BA]">{c.message}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {editingProfile && (
        <AdminEditProfileModal
          profile={editingProfile}
          onClose={() => setEditingProfile(null)}
          onSaved={load}
        />
      )}
      <Footer />
    </div>
  );
}
